import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Divider,
  CircularProgress,
  Skeleton,
  Alert,
  useMediaQuery,
  useTheme,
  Fade,
  Tooltip,
  IconButton,
  Chip
} from '@mui/material';
import {
  Group as GroupIcon,
  Store as StoreIcon,
  DateRange as DateRangeIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { listGroups, listGroupStores } from '../../api/groups';
import { listStores } from '../../api/stores';
import { getPayrollGenerate } from '../../api/payroll';

const steps = ['Select Group & Store', 'Select Date Range', 'Generate Payroll'];

const GeneratePayroll = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(14, 'day'));

  const [groups, setGroups] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [storeLoading, setStoreLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError('');
      try {
        // Prefer backend payroll generate bundle if available
        const bundle = await getPayrollGenerate();
        const bundleStores = bundle?.data?.stores || [];
        if (bundleStores.length) {
          setStores(bundleStores);
        } else {
          const storesRes = await listStores();
          setStores(storesRes?.data?.stores || []);
        }

        // Fetch groups (new endpoint)
        try {
          const groupsRes = await listGroups();
          setGroups(groupsRes?.data?.groups || []);
        } catch (_) {
          setGroups([]);
        }
        
        enqueueSnackbar('Data loaded successfully', { variant: 'success' });
      } catch (e) {
        const errorMsg = e?.response?.data?.message || 'Failed to load initial data';
        setError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [enqueueSnackbar]);

  const filteredStores = useMemo(() => {
    if (!selectedGroup) return stores;
    // When group-store mapping exists in payload, filter; otherwise show all
    return stores;
  }, [stores, selectedGroup]);

  const handleNext = () => {
    if (activeStep === 0 && (!selectedGroup || !selectedStore)) {
      enqueueSnackbar('Please select both group and store', { variant: 'warning' });
      return;
    }
    if (activeStep === 1 && (!startDate || !endDate)) {
      enqueueSnackbar('Please select both start and end dates', { variant: 'warning' });
      return;
    }
    if (activeStep === 1 && endDate.isBefore(startDate)) {
      enqueueSnackbar('End date must be after start date', { variant: 'error' });
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedGroup('');
    setSelectedStore('');
    setStartDate(dayjs());
    setEndDate(dayjs().add(14, 'day'));
    enqueueSnackbar('Form reset successfully', { variant: 'info' });
  };

  const handleGeneratePayroll = async () => {
    setGenerating(true);
    try {
      // Simulate payroll generation
      await new Promise(resolve => setTimeout(resolve, 3000));
      enqueueSnackbar('Payroll generated successfully!', { variant: 'success' });
      // In a real app, you would navigate to the payroll results page
    } catch (error) {
      enqueueSnackbar('Failed to generate payroll', { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleRefreshData = async () => {
    setLoading(true);
    try {
      const [bundleRes, groupsRes] = await Promise.all([
        getPayrollGenerate(),
        listGroups()
      ]);
      
      const bundleStores = bundleRes?.data?.stores || [];
      if (bundleStores.length) {
        setStores(bundleStores);
      } else {
        const storesRes = await listStores();
        setStores(storesRes?.data?.stores || []);
      }
      
      setGroups(groupsRes?.data?.groups || []);
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="300px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="200px" height={32} sx={{ mb: 3 }} />
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={80} sx={{ mb: 3, borderRadius: 1 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Skeleton variant="rectangular" width="48%" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="48%" height={56} sx={{ borderRadius: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Skeleton variant="rectangular" width="80px" height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="80px" height={36} sx={{ borderRadius: 1 }} />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReceiptIcon sx={{ color: '#12C1DC' }} />
              Payroll Generator
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton 
                onClick={handleRefreshData} 
                disabled={loading}
                sx={{ 
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/payroll">
              Payroll
            </Link>
            <Typography color="text.primary">Generate Payroll</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Enhanced Stepper */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ 
            borderRadius: 2, 
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151', mb: 2 }}>
                Payroll Generation Process
              </Typography>
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel={!isMobile}
                orientation={isMobile ? 'vertical' : 'horizontal'}
                sx={{ 
                  '& .MuiStepLabel-root .Mui-completed': { color: '#12C1DC' },
                  '& .MuiStepLabel-root .Mui-active': { color: '#12C1DC' },
                  '& .MuiStepConnector-line': { borderColor: '#e5e7eb' }
                }}
              >
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel 
                      StepIconComponent={({ active, completed }) => (
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: completed ? '#12C1DC' : active ? '#12C1DC' : '#e5e7eb',
                          color: completed || active ? 'white' : '#6b7280',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          {index === 0 && <GroupIcon />}
                          {index === 1 && <DateRangeIcon />}
                          {index === 2 && <ReceiptIcon />}
                        </Box>
                      )}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                        {label}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Step Content */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >

                  {activeStep === 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <GroupIcon sx={{ color: '#12C1DC', fontSize: '1.5rem' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151' }}>
                          Select Group & Store
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="group-select-label">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <GroupIcon sx={{ fontSize: '1.1rem' }} />
                                Group
                              </Box>
                            </InputLabel>
                            <Select
                              labelId="group-select-label"
                              value={selectedGroup}
                              label="Group"
                              onChange={async (e) => {
                                const value = e.target.value;
                                setSelectedGroup(value);
                                setSelectedStore('');
                                setStoreLoading(true);
                                
                                // If we have a group id (Mongo _id), load stores for that group
                                if (value) {
                                  try {
                                    const res = await listGroupStores(value);
                                    const groupStores = res?.data?.stores || [];
                                    if (groupStores.length) {
                                      setStores(groupStores);
                                      enqueueSnackbar(`Loaded ${groupStores.length} stores for selected group`, { variant: 'info' });
                                    }
                                  } catch (error) {
                                    enqueueSnackbar('Failed to load stores for selected group', { variant: 'error' });
                                  }
                                }
                                setStoreLoading(false);
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': { borderColor: '#12C1DC' },
                                  '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                                }
                              }}
                            >
                              {groups.map(g => (
                                <MenuItem key={g._id} value={g._id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GroupIcon sx={{ fontSize: '1rem', color: '#12C1DC' }} />
                                    {g.name}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="store-select-label">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StoreIcon sx={{ fontSize: '1.1rem' }} />
                                Store
                              </Box>
                            </InputLabel>
                            <Select
                              labelId="store-select-label"
                              value={selectedStore}
                              label="Store"
                              onChange={(e) => setSelectedStore(e.target.value)}
                              disabled={!selectedGroup || storeLoading}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': { borderColor: '#12C1DC' },
                                  '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                                }
                              }}
                            >
                              {storeLoading ? (
                                <MenuItem disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    Loading stores...
                                  </Box>
                                </MenuItem>
                              ) : (
                                filteredStores.map(s => (
                                  <MenuItem key={s._id || s.store} value={s.store}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <StoreIcon sx={{ fontSize: '1rem', color: '#12C1DC' }} />
                                      <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                          {s.store}
                                        </Typography>
                                        {s.location && (
                                          <Typography variant="caption" color="text.secondary">
                                            {s.location}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      {/* Selection Summary */}
                      {(selectedGroup || selectedStore) && (
                        <Card sx={{ mt: 3, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                          <CardContent sx={{ py: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: '#0369a1', mb: 1, fontWeight: 600 }}>
                              Current Selection
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              {selectedGroup && (
                                <Chip 
                                  icon={<GroupIcon />}
                                  label={groups.find(g => g._id === selectedGroup)?.name || 'Selected Group'}
                                  size="small"
                                  sx={{ bgcolor: '#dbeafe', color: '#1d4ed8' }}
                                />
                              )}
                              {selectedStore && (
                                <Chip 
                                  icon={<StoreIcon />}
                                  label={selectedStore}
                                  size="small"
                                  sx={{ bgcolor: '#dcfce7', color: '#166534' }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  )}

                  {activeStep === 1 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <DateRangeIcon sx={{ color: '#12C1DC', fontSize: '1.5rem' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151' }}>
                          Select Date Range
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="Start Date"
                              value={startDate}
                              onChange={(newValue) => setStartDate(newValue)}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  fullWidth 
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover fieldset': { borderColor: '#12C1DC' },
                                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                                    }
                                  }}
                                />
                              )}
                              sx={{ width: '100%' }}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="End Date"
                              value={endDate}
                              onChange={(newValue) => setEndDate(newValue)}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  fullWidth 
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover fieldset': { borderColor: '#12C1DC' },
                                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                                    }
                                  }}
                                />
                              )}
                              sx={{ width: '100%' }}
                              minDate={startDate}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </Grid>

                      {/* Date Range Summary */}
                      {startDate && endDate && (
                        <Card sx={{ mt: 3, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <CardContent sx={{ py: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: '#15803d', mb: 2, fontWeight: 600 }}>
                              Payroll Period Summary
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DateRangeIcon sx={{ color: '#16a34a', fontSize: '1.2rem' }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Period</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {endDate.diff(startDate, 'day') + 1} days
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ScheduleIcon sx={{ color: '#16a34a', fontSize: '1.2rem' }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Start Date</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {startDate.format('MMM DD, YYYY')}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ScheduleIcon sx={{ color: '#16a34a', fontSize: '1.2rem' }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">End Date</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {endDate.format('MMM DD, YYYY')}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  )}

                  {activeStep === 2 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <ReceiptIcon sx={{ color: '#12C1DC', fontSize: '1.5rem' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151' }}>
                          Generate Payroll
                        </Typography>
                      </Box>
                      
                      {/* Enhanced Payroll Summary */}
                      <Card sx={{ mb: 3, bgcolor: '#fefbff', border: '1px solid #e4c1f9', borderRadius: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <ReceiptIcon sx={{ color: '#9333ea', fontSize: '1.3rem' }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7c3aed' }}>
                              Payroll Summary
                            </Typography>
                          </Box>
                          <Divider sx={{ mb: 3 }} />
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <GroupIcon sx={{ color: '#6b7280', fontSize: '1.1rem' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  Group:
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={600}>
                                {groups.find(g => g._id === selectedGroup)?.name || 'N/A'}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <StoreIcon sx={{ color: '#6b7280', fontSize: '1.1rem' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  Store:
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={600}>
                                {selectedStore || 'N/A'}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DateRangeIcon sx={{ color: '#6b7280', fontSize: '1.1rem' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  Period:
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={600}>
                                {startDate.format('MMM DD')} - {endDate.format('MMM DD, YYYY')}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PeopleIcon sx={{ color: '#6b7280', fontSize: '1.1rem' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  Total Employees:
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={600} sx={{ color: '#059669' }}>
                                24
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <ScheduleIcon sx={{ color: '#6b7280', fontSize: '1.1rem' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  Total Hours:
                                </Typography>
                              </Box>
                              <Typography variant="body1" fontWeight={600} sx={{ color: '#0369a1' }}>
                                1,248
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <MoneyIcon sx={{ color: '#6b7280', fontSize: '1.1rem' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  Estimated Total:
                                </Typography>
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc2626' }}>
                                ₱156,000.00
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                      
                      {/* Generate Button */}
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={generating}
                        onClick={handleGeneratePayroll}
                        startIcon={generating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <ReceiptIcon />}
                        sx={{
                          bgcolor: '#12C1DC',
                          '&:hover': { bgcolor: '#0FABDB' },
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: 2
                        }}
                      >
                        {generating ? 'Generating Payroll...' : 'Generate Payroll'}
                      </Button>
                    </Box>
                  )}
                </motion.div>
              </AnimatePresence>
            </Box>

            {/* Enhanced Navigation */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 4,
              pt: 3,
              borderTop: '1px solid #e5e7eb'
            }}>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{
                  textTransform: 'none',
                  borderColor: '#e5e7eb',
                  color: '#6b7280',
                  '&:hover': { 
                    borderColor: '#12C1DC',
                    bgcolor: 'rgba(18, 193, 220, 0.05)'
                  }
                }}
              >
                Back
              </Button>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {activeStep === steps.length - 1 && (
                  <Button 
                    variant="outlined" 
                    onClick={handleReset} 
                    sx={{ 
                      mr: 1,
                      textTransform: 'none',
                      borderColor: '#e5e7eb',
                      color: '#6b7280',
                      '&:hover': { 
                        borderColor: '#f59e0b',
                        bgcolor: 'rgba(245, 158, 11, 0.05)'
                      }
                    }}
                  >
                    Reset
                  </Button>
                )}
                
                {activeStep < steps.length - 1 && (
                  <Button 
                    variant="contained" 
                    onClick={handleNext}
                    sx={{
                      bgcolor: '#12C1DC',
                      '&:hover': { bgcolor: '#0FABDB' },
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Fade>
  );
};

export default GeneratePayroll;
